/**
 * Formats a number with a .NET custom numeric format string, as the legacy app does with
 * `decimal.ToString(format)`: `0` is a required digit, `#` an optional one, `.` the decimal point
 * and `,` before the decimal point turns on digit grouping. Separators follow the locale, as .NET
 * follows the current culture. Literal text in the format is not supported (no dashboard uses it).
 */
export function formatNumber(value: number, format: string, locale?: string) : string {
    const [integerPart = '', fractionPart = ''] = format.split('.');
    const requiredFraction = (fractionPart.match(/0/g) ?? []).length;
    const optionalFraction = (fractionPart.match(/#/g) ?? []).length;
    const requiredInteger = Math.max(1, (integerPart.match(/0/g) ?? []).length);

    const formatter = new Intl.NumberFormat(locale, {
        minimumIntegerDigits: requiredInteger,
        minimumFractionDigits: requiredFraction,
        maximumFractionDigits: requiredFraction + optionalFraction,
        useGrouping: integerPart.includes(','),
    });

    return formatter.format(value);
}

/** The most decimals a format shows: `0.0#` shows two, `#,##0` none. */
export function formatDecimals(format: string) : number {
    return ((format.split('.')[1] ?? '').match(/[0#]/g) ?? []).length;
}

/**
 * Formats a number as `decimal.ToString()` without a format does: every digit it has, no
 * grouping, the locale's decimal separator. The digits are JavaScript's shortest form of the
 * number, which for a value parsed from text are the digits of that text.
 */
export function formatPlainNumber(value: number, locale?: string) : string {
    const separator = new Intl.NumberFormat(locale).formatToParts(1.5).find((part) => part.type === 'decimal')?.value ?? '.';
    const text = Number.isInteger(value) || Math.abs(value) >= 1e-6 ? String(value) : value.toFixed(20).replace(/0+$/, '');

    return text.replace('.', separator);
}
